import { useState, useEffect, useCallback, useMemo } from 'react'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { isMatterFilesDeleteEligible, type ExternalStorageLinkedItem } from '@/user_files/external-storage/utils/external-storage-items'
import { fileProcessingStatusToDriveStatus } from '@/user_files/utils/drive-list-format'
import { isWaitingForOcrQuota } from '@/user_files/utils/processing-status'

export interface FileForSelection extends ExternalStorageLinkedItem {
  id: string
  folder_id: string
  original_filename: string
  processing_status?: string | null
  processing_blocked_reason?: string | null
  extracted_from_file_id?: string | null
}

export interface FolderForSelection extends ExternalStorageLinkedItem {
  id: string
  parent_id: string | null
  path: string
  name: string
}

/**
 * Selection state for drive rows.
 *
 * @param files - The file selection universe for the current folder or search results. Callers may
 *   include collapsed extracted attachments that the table is not painting yet; select-all and
 *   Ask Assistant intentionally cover those hidden children so an email's attachments can be sent
 *   without expanding the tree. Delete/move still use selectedMutableFileIds (roots only).
 * @param folders - The folder selection universe; select-all covers exactly these.
 * @param resetKey - Identifies what is on screen — the folder id while browsing, the query while
 *   showing search results. Selection resets whenever it changes, because the rows it referred to
 *   are gone.
 */
export function useDriveSelection(files: FileForSelection[], folders: FolderForSelection[], resetKey: string | null) {
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([])

  // Clear selection when the visible row set changes
  useEffect(() => {
    setSelectedFileIds([])
    setSelectedFolderIds([])
  }, [resetKey])

  // Selection covers Ask Assistant and mutations. Grouped attachments are selectable so they can
  // be sent to the assistant, but delete/move still operate only on mutable roots (see
  // selectedMutableFileIds). Detached external items may be selected; managed or rollout-unknown
  // associations stay excluded. Move applies its stricter all-local rule separately. Files waiting
  // for OCR quota are selectable so they can be deleted; Ask Assistant skips them as unprocessed.
  const selectableFileIds = useMemo(
    () =>
      files
        .filter((file) => {
          if (!isMatterFilesDeleteEligible(file)) return false
          if (isWaitingForOcrQuota(file)) return true
          const rowStatus = fileProcessingStatusToDriveStatus(file.processing_status)
          return rowStatus !== DriveListItemStatus.Processing && rowStatus !== DriveListItemStatus.Pending
        })
        .map((file) => file.id),
    [files]
  )
  const mutableFileIdSet = useMemo(() => new Set(files.filter((file) => !file.extracted_from_file_id).map((file) => file.id)), [files])
  const selectableFolderIds = useMemo(() => folders.filter(isMatterFilesDeleteEligible).map((folder) => folder.id), [folders])
  const selectableFileIdSet = useMemo(() => new Set(selectableFileIds), [selectableFileIds])
  const selectableFolderIdSet = useMemo(() => new Set(selectableFolderIds), [selectableFolderIds])
  const selectedSelectableFileIds = useMemo(() => selectedFileIds.filter((fileId) => selectableFileIdSet.has(fileId)), [selectedFileIds, selectableFileIdSet])
  const selectedSelectableFileIdSet = useMemo(() => new Set(selectedSelectableFileIds), [selectedSelectableFileIds])
  /** Selected files that delete/move may act on — excludes actively grouped attachments. */
  const selectedMutableFileIds = useMemo(
    () => selectedSelectableFileIds.filter((fileId) => mutableFileIdSet.has(fileId)),
    [selectedSelectableFileIds, mutableFileIdSet]
  )
  const selectedSelectableFolderIds = useMemo(
    () => selectedFolderIds.filter((folderId) => selectableFolderIdSet.has(folderId)),
    [selectedFolderIds, selectableFolderIdSet]
  )
  const selectedFolderIdSet = useMemo(() => new Set(selectedSelectableFolderIds), [selectedSelectableFolderIds])

  // Drop items from selection when they become non-selectable (e.g. files enter
  // pending/processing, or refreshed metadata says an external item is managed again).
  // Returning `prev` unchanged when nothing dropped keeps this from looping
  // when callers pass a new files/folders array identity every render.
  useEffect(() => {
    setSelectedFileIds((prev) => {
      const next = prev.filter((fileId) => selectableFileIdSet.has(fileId))
      return next.length === prev.length ? prev : next
    })
  }, [selectableFileIdSet])
  useEffect(() => {
    setSelectedFolderIds((prev) => {
      const next = prev.filter((folderId) => selectableFolderIdSet.has(folderId))
      return next.length === prev.length ? prev : next
    })
  }, [selectableFolderIdSet])

  const isFileSelected = useCallback((fileId: string) => selectedSelectableFileIdSet.has(fileId), [selectedSelectableFileIdSet])

  const toggleFileSelect = useCallback((fileId: string) => {
    setSelectedFileIds((prev) => (prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]))
  }, [])

  const isFolderSelected = useCallback((folderId: string) => selectedFolderIdSet.has(folderId), [selectedFolderIdSet])

  const toggleFolderSelect = useCallback((folderId: string) => {
    setSelectedFolderIds((prev) => (prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]))
  }, [])

  const selectedCount = selectedSelectableFileIds.length + selectedSelectableFolderIds.length

  const isAllSelected = useMemo(() => {
    const total = selectableFileIds.length + selectableFolderIds.length
    if (total === 0) return false
    return selectableFileIds.every((id) => selectedSelectableFileIdSet.has(id)) && selectableFolderIds.every((id) => selectedFolderIdSet.has(id))
  }, [selectableFileIds, selectableFolderIds, selectedSelectableFileIdSet, selectedFolderIdSet])

  const selectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedFileIds([])
      setSelectedFolderIds([])
    } else {
      setSelectedFileIds(selectableFileIds)
      setSelectedFolderIds(selectableFolderIds)
    }
  }, [isAllSelected, selectableFileIds, selectableFolderIds])

  const clearSelection = useCallback(() => {
    setSelectedFileIds([])
    setSelectedFolderIds([])
  }, [])

  return {
    selectedSelectableFileIds,
    selectedMutableFileIds,
    selectedFolderIds: selectedSelectableFolderIds,
    isFileSelected,
    toggleFileSelect,
    isFolderSelected,
    toggleFolderSelect,
    selectedCount,
    isAllSelected,
    selectAll,
    clearSelection,
  }
}
